import React, { useEffect, useRef } from "react";

export default function ProjectMap({ lat, lng, title, address }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!lat || !lng || !mapContainerRef.current) return;

    // Load Leaflet CSS
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    const initMap = () => {
      const L = window.L;
      if (!L || !mapContainerRef.current) return;

      // Clean up previous instance
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }

      // Initialize map
      const map = L.map(mapContainerRef.current, {
        center: [lat, lng],
        zoom: 16,
        zoomControl: true,
        scrollWheelZoom: false
      });
      mapInstanceRef.current = map;

      // Official Singapore SLA OneMap Base Map Tile Layer
      const oneMapUrl = "https://www.onemap.gov.sg/maps/tiles/Default/{z}/{x}/{y}.png";

      L.tileLayer(oneMapUrl, {
        minZoom: 11,
        maxZoom: 19,
        attribution: 'Map data &copy; <a href="https://www.onemap.gov.sg" target="_blank">OneMap</a> Singapore Land Authority'
      }).addTo(map);

      // Custom Red Pin Marker Icon
      const customPinHtml = `
        <div style="position: relative; width: 44px; height: 54px; display: flex; align-items: center; justify-content: center;">
          <div style="position: absolute; width: 40px; height: 40px; background: rgba(225, 29, 72, 0.25); border-radius: 50%; animation: ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite;"></div>
          <div style="position: relative; z-index: 10; width: 36px; height: 46px; filter: drop-shadow(0 6px 12px rgba(0,0,0,0.4));">
            <svg viewBox="0 0 384 512" fill="#E11D48" width="100%" height="100%">
              <path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z" stroke="#FFFFFF" stroke-width="16" />
            </svg>
            <div style="position: absolute; top: 12px; left: 50%; transform: translateX(-50%); width: 14px; height: 14px; background: #FFFFFF; border-radius: 50%;"></div>
          </div>
        </div>
      `;

      const customIcon = L.divIcon({
        html: customPinHtml,
        className: "custom-project-pin",
        iconSize: [44, 54],
        iconAnchor: [22, 54],
        popupAnchor: [0, -48]
      });

      // Add Marker
      const marker = L.marker([lat, lng], { icon: customIcon }).addTo(map);

      // Popup Content
      const popupHtml = `
        <div style="font-family: sans-serif; padding: 4px; max-width: 220px;">
          <div style="font-size: 13px; font-weight: 700; color: #0A1128; margin-bottom: 2px;">${title || "Project Location"}</div>
          <div style="font-size: 11px; color: #475569; line-height: 1.3;">${address || "Singapore"}</div>
        </div>
      `;
      marker.bindPopup(popupHtml).openPopup();
    };

    if (window.L) {
      initMap();
    } else {
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = initMap;
      document.body.appendChild(script);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [lat, lng, title, address]);

  return (
    <div className="relative w-full h-[400px] rounded-xl overflow-hidden border border-[#E7E2D6] shadow-inner bg-slate-100">
      <div ref={mapContainerRef} className="w-full h-full z-10" />
      <style>{`
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        .custom-project-pin {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
}
